export type ItemStatus = "todo" | "in_progress" | "done";

export interface ChecklistItem {
  key: string;
  label: string;
  defaultStatus?: ItemStatus;
  defaultNote?: string;
}

export interface ChecklistSection {
  id: string;
  title: string;
  items: ChecklistItem[];
}

export const HIGHEST_LEVERAGE: { key: string; label: string }[] = [
  { key: "obs_overall", label: "Observability — Sentry + uptime + product analytics" },
  { key: "comms_overall", label: "Email / newsletter — biggest retention + partner-touch lever" },
];

export const INFRA_SECTIONS: ChecklistSection[] = [
  {
    id: "legal",
    title: "1. Legal & Governance",
    items: [
      { key: "legal_policies", label: "Privacy Policy, ToS, Cookie Policy, Acceptable Use Policy", defaultStatus: "done", defaultNote: "Live at /privacy, /terms, /cookies, /acceptable-use. Starter templates — recommend counsel review before relying on IP/trade-secret and DMCA clauses." },
      { key: "legal_dpa", label: "DPA template (agencies require)", defaultStatus: "done", defaultNote: "Template DPA live at /dpa, linked in footer. Covers GDPR/UK GDPR/CCPA roles, SCCs by reference, sub-processors, security measures, breach notification, audits, return/deletion. Recommend counsel review before signing with enterprise/agency customers." },
      { key: "legal_retention", label: "Records retention + deletion policy", defaultStatus: "done", defaultNote: "Public policy live at /retention. Self-serve A/B (anonymize vs purge) wired into delete-account flow. Daily retention-sweep-accounts cron warns at 35mo, final-notices at 36mo, auto-deletes empty accounts, queues accounts with content into Admin → Retention. Remaining follow-up: 90-day operational log purge cron." },
      { key: "legal_incident", label: "Incident response plan (1-pager OK early)", defaultStatus: "done", defaultNote: "Internal runbook at docs/runbooks/incident-response.md (severity levels, scenario playbooks for leaked secret / RLS bypass / admin compromise / outage / abuse, comms templates, 7-day post-mortem). Public commitments at /security with 72h breach-notification target. Admin Dashboard now surfaces a Security & incidents strip (open incidents, 7d retention actions, link to admin auth failures in Sentry). Follow-ups: name a backup IC, run the first tabletop exercise, and add a real incidents table once volume justifies it." },
      { key: "legal_entity", label: "Entity structure: LLC vs PBC vs nonprofit arm" },
    ],
  },
  {
    id: "observability",
    title: "2. Observability & Reliability",
    items: [
      { key: "obs_sentry_fe", label: "Error monitoring — Sentry (frontend)", defaultStatus: "done", defaultNote: "Initialized in src/main.tsx" },
      { key: "obs_sentry_edge", label: "Error monitoring — Sentry (edge functions)", defaultStatus: "done", defaultNote: "reportEdgeError helper in all 4 functions" },
      { key: "obs_uptime", label: "Uptime + public status page (BetterStack)", defaultStatus: "done", defaultNote: "/status route live" },
      { key: "obs_logging", label: "Structured logging + retention for edge functions", defaultStatus: "in_progress", defaultNote: "Shared JSON logger at _shared/logger.ts; wired into process-email-queue. Long-term retention requires forwarding (Logflare/Datadog) — separate decision." },
      { key: "obs_backup_drill", label: "Documented backup restore drill", defaultStatus: "done", defaultNote: "Runbook at docs/runbooks/backup-restore-drill.md — quarterly cadence" },
      { key: "obs_cwv", label: "Core Web Vitals / performance monitoring", defaultStatus: "done", defaultNote: "web-vitals reported to Sentry as measurements + breadcrumbs" },
      { key: "obs_error_boundary", label: "Sentry.ErrorBoundary with branded fallback", defaultStatus: "done", defaultNote: "Wraps <App /> in src/main.tsx; shows reference ID" },
      { key: "obs_sourcemaps", label: "Source maps upload + release tagging", defaultStatus: "done", defaultNote: "@sentry/vite-plugin uploads when SENTRY_AUTH_TOKEN set; release tagged via VITE_APP_VERSION" },
    ],
  },
  {
    id: "analytics",
    title: "3. Analytics & Measurement",
    items: [
      { key: "analytics_product", label: "Product analytics: PostHog or Plausible" },
      { key: "analytics_funnels", label: "Conversion funnels (signup, grant save, idea submission)" },
      { key: "analytics_cohorts", label: "Cohort + retention dashboards" },
      { key: "analytics_utm", label: "UTM / partner attribution" },
    ],
  },
  {
    id: "comms",
    title: "4. Communications",
    items: [
      { key: "comms_email", label: "Transactional email w/ verified domain (SPF/DKIM/DMARC)" },
      { key: "comms_newsletter", label: "Weekly grants digest newsletter" },
      { key: "comms_inapp", label: "In-app notifications (deadlines, tracked-county bills)" },
      { key: "comms_sms", label: "SMS opt-in (Twilio)" },
    ],
  },
  {
    id: "data",
    title: "5. Content & Data Pipeline",
    items: [
      { key: "data_sot", label: "Source-of-truth docs for every dataset" },
      { key: "data_cron", label: "Cron'd refresh schedules with failure alerts" },
      { key: "data_editorial", label: "Editorial workflow: draft → review → publish" },
      { key: "data_citations", label: "Citation/source linking standard" },
    ],
  },
  {
    id: "a11y",
    title: "6. Accessibility & Localization",
    items: [
      { key: "a11y_audit", label: "WCAG 2.1 AA audit (axe + Lighthouse CI)" },
      { key: "a11y_es", label: "Spanish translation path (Birmingham, Mobile)" },
      { key: "a11y_plain", label: "Plain-language pass on civic content (~8th grade)" },
    ],
  },
  {
    id: "integrations",
    title: "7. Partnerships & Integrations",
    items: [
      { key: "int_api", label: "Public API / webhooks for partners" },
      { key: "int_sso", label: "SSO (SAML/OIDC) for enterprise/agency" },
      { key: "int_widgets", label: "Embeddable widgets (county map, grant feed)" },
      { key: "int_zapier", label: "Zapier / Make connector" },
    ],
  },
  {
    id: "revenue",
    title: "8. Revenue Infrastructure",
    items: [
      { key: "rev_stripe", label: "Stripe — paid tiers, sponsored listings, event tickets" },
      { key: "rev_invoicing", label: "Invoicing + W-9 collection for agency contracts" },
      { key: "rev_donations", label: "Donation flow (if 501(c)(3) sister entity)" },
    ],
  },
  {
    id: "ops",
    title: "9. Internal Operations",
    items: [
      { key: "ops_crm", label: "CRM (HubSpot free / Attio) for partner + funder pipeline" },
      { key: "ops_inbox", label: "Shared inbox (info@emrkt.co) with response SLAs" },
      { key: "ops_help", label: "Public help center / FAQ" },
      { key: "ops_playbook", label: "Partner & advocate onboarding playbook" },
    ],
  },
  {
    id: "trust",
    title: "10. On-site Trust Signals",
    items: [
      { key: "trust_pages", label: "/trust, /security, /accessibility pages" },
      { key: "trust_roadmap", label: "Public roadmap / changelog" },
    ],
  },
];